import { logout, me, type CurrentUser } from '@itc/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, ApiError } from './api.ts';

/** The logged-in user and their effective permissions, loaded from /api/me. */
export const useUserStore = defineStore('itc-user', () => {
  const user = ref<CurrentUser | null>(null);
  const permissions = ref<string[]>([]);
  /** When the login stops counting as recent; null when unknown. */
  const recentLoginUntil = ref<Date | null>(null);
  /** The enabled modules still in their trial, whose pages carry the trial banner. */
  const trials = ref<{ name: string; title: string }[]>([]);
  const loaded = ref(false);
  let loading: Promise<void> | undefined;

  async function load(): Promise<void> {
    try {
      const result = await api(me, {}, { redirectOnUnauthorized: false });
      user.value = result.user;
      permissions.value = result.permissions;
      recentLoginUntil.value = result.recentLoginUntil === null ? null : new Date(result.recentLoginUntil);
      trials.value = result.trials;
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) throw error;
      user.value = null;
      permissions.value = [];
      recentLoginUntil.value = null;
      trials.value = [];
    } finally {
      loaded.value = true;
    }
  }

  /** Loads once; later calls reuse the result. */
  async function ensureLoaded(): Promise<void> {
    if (loaded.value) return;
    loading ??= load().finally(() => {
      loading = undefined;
    });
    await loading;
  }

  async function signOut(): Promise<void> {
    await api(logout, {}, { redirectOnUnauthorized: false });
    user.value = null;
    permissions.value = [];
    recentLoginUntil.value = null;
    trials.value = [];
  }

  /**
   * Whether sensitive changes (`recentLogin` endpoints) will be accepted now. Call it
   * when showing a form; it reads the clock, so it isn't reactive on its own.
   */
  function hasRecentLogin(now: Date = new Date()): boolean {
    return recentLoginUntil.value !== null && recentLoginUntil.value.getTime() > now.getTime();
  }

  /** Whether the user holds a permission. The server stays authoritative; this only hides UI. */
  function can(permission: string): boolean {
    if (permission === 'public') return true;
    if (user.value === null) return false;
    if (permission === 'authenticated') return true;
    return permissions.value.includes('*') || permissions.value.includes(permission);
  }

  return { user, permissions, recentLoginUntil, trials, loaded, isLoggedIn: computed(() => user.value !== null), load, ensureLoaded, signOut, can, hasRecentLogin };
});

export function useUser() {
  return useUserStore();
}
